import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import { useWholeMoneyField } from "../../components/MoneyInput";
import { useToast } from "../../components/Toast";
import type { CatalogHit, Category } from "../../api/types";

// 品項庫新增(業主原話:「品項庫裡面應該要有可以新增品項的按鈕:分類 品名 規格 單位 單價」)。
//
// 一個視窗填完五欄,後端依序建立/沿用 分類 → 品項 → 規格。
// **分類與品名是可輸入的下拉**:既有的列出來供沿用,也能直接打新的 ——
// 業主的項目五花八門,不在既有分類裡的東西照樣要建得起來(規格書 §3.1(3):
// 品項庫是輸入輔助,不是受控型錄)。
//
// 價格掛在「規格」層:同一個品項底下可以有多種規格各自定價,所以同一個品名
// 打第二次不是重複,而是替它多加一種規格。真正不准重複的是「同品項下的同規格」,
// 那一種後端回 409,對話框留著讓人改規格就好。

/** 品名下拉最多列幾個既有品名。太多反而找不到,打字打新的更快。 */
const NAME_SUGGEST_LIMIT = 50;

/** 單位的預設值。業主的報價單九成以上是「式」。 */
const DEFAULT_UNIT = "式";

/**
 * 單價的預設值。**刻意是 "0" 而不是空字串**:沒填就是 0 元,
 * 那就讓畫面直接寫著 0,不要留一格空白、送出時才悄悄變成 0。
 */
const DEFAULT_PRICE = "0";

/** 去重且保留原順序。分類與品名的建議都靠它。 */
function uniqueNames(names: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const name of names) {
    const trimmed = name.trim();
    if (trimmed === "" || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

export interface CatalogCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** 建立成功後把那一筆交出去,呼叫端可以直接帶進報價單。 */
  onCreated?: (hit: CatalogHit) => void;
}

export function CatalogCreateDialog({ open, onClose, onCreated }: CatalogCreateDialogProps) {
  const { push } = useToast();
  const queryClient = useQueryClient();
  const fieldId = useId();
  const categoryListId = `${fieldId}-categories`;
  const nameListId = `${fieldId}-names`;

  const [category, setCategory] = useState("");
  const [itemName, setItemName] = useState("");
  const [spec, setSpec] = useState("");
  const [unit, setUnit] = useState(DEFAULT_UNIT);
  const [price, setPrice] = useState(DEFAULT_PRICE);
  const [error, setError] = useState<string | null>(null);
  const categoryRef = useRef<HTMLInputElement>(null);

  // 單價欄的行為(千分位、整數化、游標還原)一律走 MoneyInput 那一份實作。
  // 這裡不再自己留一套格式化 —— 兩份平行實作遲早會對同一串輸入給出兩種答案。
  const priceField = useWholeMoneyField(price, setPrice);

  // 每次開啟都從空白開始,上一次打到一半的東西不會殘留。
  useEffect(() => {
    if (!open) return;
    setCategory("");
    setItemName("");
    setSpec("");
    setUnit(DEFAULT_UNIT);
    setPrice(DEFAULT_PRICE);
    setError(null);
  }, [open]);

  // 開啟後把焦點送進第一格。**刻意不用 autoFocus**:那是在 commit 階段就搶焦點,
  // 早於 Dialog 的 useRestoreFocus 記下「原本焦點在哪顆按鈕」,關閉時 previous
  // 指向的是已經卸載的輸入格,document.contains 為 false,還原整個被跳過 ——
  // 焦點掉到 <body>,鍵盤使用者按完 Esc 得從頁首重新 Tab 一次才回得到品項庫。
  // 子元件 Dialog 的 effect 會先跑,先記下觸發鈕,這裡才把焦點送進來。
  useEffect(() => {
    if (!open) return;
    categoryRef.current?.focus();
  }, [open]);

  // 既有分類與既有品名。關著的時候不打後端。
  const { data: categoryData } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get<Category[]>("/categories"),
    enabled: open,
  });
  const { data: hitData } = useQuery({
    queryKey: ["catalog", ""],
    queryFn: () => api.get<CatalogHit[]>("/catalog/search", { q: "", limit: NAME_SUGGEST_LIMIT }),
    enabled: open,
  });

  const categoryNames = useMemo(
    () => uniqueNames((Array.isArray(categoryData) ? categoryData : []).map((row) => row.name)),
    [categoryData],
  );

  // 品名的建議跟著分類走:分類打的是既有分類就只列那一類的品名,
  // 打的是新分類就把全部列出來供參考。
  const itemNames = useMemo(() => {
    const hits = Array.isArray(hitData) ? hitData : [];
    const picked = category.trim();
    const scoped = picked !== "" && hits.some((hit) => hit.category === picked)
      ? hits.filter((hit) => hit.category === picked)
      : hits;
    return uniqueNames(scoped.map((hit) => hit.item_name));
  }, [hitData, category]);

  // 三個識別欄位(分類 / 品名 / 規格)缺一不可,只打空白也算沒填 ——
  // 不然會建出一個叫「   」的分類,之後誰也選不到它。
  //
  // 這三欄同時掛 required 與寫著「必填」的 hint(hint 會經 aria-describedby 綁上去)。
  // 只把送出鈕設成 disabled 是不夠的:disabled 的按鈕不進 Tab 序,螢幕閱讀器
  // 使用者填到一半 Tab 到表單尾端,取消鈕之後就沒有東西了,既沒有錯誤提示、
  // 也沒有任何欄位被標成必填,根本不知道少了什麼。
  const canSubmit = category.trim() !== "" && itemName.trim() !== "" && spec.trim() !== "";

  const create = useMutation({
    mutationFn: () =>
      api.post<CatalogHit>("/catalog/items", {
        category_name: category.trim(),
        item_name: itemName.trim(),
        spec: spec.trim(),
        unit: unit.trim(),
        price,
      }),
    onSuccess: (hit) => {
      push(`已把「${hit.item_name}」加進品項庫`, "success");
      queryClient.invalidateQueries({ queryKey: ["catalog"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      onCreated?.(hit);
      onClose();
    },
    // 失敗絕不關閉對話框:關掉等於把剛打的五欄丟掉,只能重打一次。
    // 後端的原句照抄(例如規格撞名的 409),前端不自己另編一句。
    onError: (err) => setError(err instanceof ApiError ? err.message : "無法連線到伺服器,請稍後再試。"),
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || create.isPending) return;
    setError(null);
    create.mutate();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="新增品項"
      maxWidth="max-w-md"
      // 打到一半誤點背景就整份消失太傷,關閉一律走 Esc 或明確的取消 / 關閉鈕。
      closeOnBackdrop={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <Input
          label="分類"
          name="category_name"
          list={categoryListId}
          autoComplete="off"
          ref={categoryRef}
          required
          hint="必填。可以選既有分類,也可以直接打新的"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        />
        <datalist id={categoryListId}>
          {categoryNames.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>

        <Input
          label="品名"
          name="item_name"
          list={nameListId}
          autoComplete="off"
          required
          hint="必填。同一個品名底下可以有多種規格各自定價"
          value={itemName}
          onChange={(event) => setItemName(event.target.value)}
        />
        <datalist id={nameListId}>
          {itemNames.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>

        <Input
          label="規格"
          name="spec"
          autoComplete="off"
          required
          hint="必填"
          value={spec}
          onChange={(event) => setSpec(event.target.value)}
        />

        <div className="flex gap-3">
          <div className="w-28">
            <Input
              label="單位"
              name="unit"
              autoComplete="off"
              value={unit}
              onChange={(event) => setUnit(event.target.value)}
            />
          </div>
          <div className="min-w-0 flex-1">
            <Input
              label="單價"
              name="price"
              numeric
              hint="只收整數,有小數會四捨五入"
              ref={priceField.ref}
              {...priceField.props}
            />
          </div>
        </div>

        {error ? (
          <p
            role="alert"
            className="m-0 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--danger)]"
          >
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={create.isPending}>
            取消
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            loading={create.isPending}
            icon={<Plus aria-hidden="true" size={16} />}
          >
            建立品項
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
